import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Tv, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivatePage() {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const refs = useRef([]);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const c = (searchParams.get('code') || '').replace(/\D/g, '').slice(0, 6);
    if (c.length === 6) {
      setDigits(c.split(''));
      submitCode(c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (i, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setError('');
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'Enter') submit();
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (text.length) {
      e.preventDefault();
      const next = text.split('').concat(Array(6).fill('')).slice(0, 6);
      setDigits(next);
      refs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  const submitCode = async (code) => {
    if (code.length !== 6) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/tv/pair/activate`, { code }, { withCredentials: true });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    if (busy) return;
    submitCode(digits.join(''));
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
      <header className="px-6 sm:px-12 py-5">
        <Link to="/" data-testid="activate-logo-link">
          <img src="https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png" alt="Maya+" className="h-8" />
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center" data-testid="activate-page">
          {success ? (
            <div className="flex flex-col items-center gap-4" data-testid="activate-success">
              <CheckCircle2 className="w-16 h-16 text-green-400" />
              <h1 className="text-2xl sm:text-3xl font-bold text-white">TV connectée !</h1>
              <p className="text-white/60">Votre téléviseur va se connecter automatiquement dans quelques secondes. Vous pouvez retourner devant votre TV.</p>
              <Link to="/" className="mt-4 px-6 py-2.5 rounded-full bg-[#E9641A] text-white font-semibold hover:bg-[#d55812] transition-colors" data-testid="activate-back-home">
                Retour à l'accueil
              </Link>
            </div>
          ) : (
            <>
              <div className="flex justify-center mb-5">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
                  <Tv className="w-7 h-7 text-[#E9641A]" />
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Connecter votre TV</h1>
              <p className="text-white/60 mb-8">Entrez le code à 6 chiffres affiché sur l'écran de votre téléviseur.</p>

              <div className="flex justify-center gap-2 sm:gap-3 mb-6" onPaste={handlePaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => refs.current[i] = el}
                    value={d}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    inputMode="numeric"
                    autoFocus={i === 0}
                    className="w-11 h-14 sm:w-13 sm:h-16 text-center text-2xl font-bold rounded-lg bg-white/10 border border-white/25 text-white focus:outline-none focus:border-[#E9641A] focus:ring-1 focus:ring-[#E9641A] transition-colors"
                    data-testid={`activate-code-input-${i}`}
                  />
                ))}
              </div>

              {error && <p className="text-red-400 text-sm mb-4" data-testid="activate-error">{error}</p>}

              <button
                onClick={submit}
                disabled={digits.join('').length !== 6 || busy}
                className="w-full py-3 rounded-full bg-[#E9641A] text-white font-semibold text-base hover:bg-[#d55812] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="activate-submit-btn"
              >
                {busy ? 'Vérification…' : 'Connecter la TV'}
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
